/**
 * The arithmetic of others' presence on the canvas (AMO-178), ported from
 * AmoregGTM's `features/workflow/presence.tsx`: which pointers to draw (and
 * when one has been still long enough to put away), and where the steps
 * others drag are drawn, gliding between their updates and standing where
 * they were let go until the drop arrives as a saved version. Pure: the
 * caller passes the clock.
 *
 * @module components/workflows/canvas/presenceTracks
 */
import type { WorkflowPeer } from "@amoreg/contracts";

import type { Point } from "./geometry";

/** A pointer that has not moved for this long is put away. */
export const CURSOR_IDLE_MS = 10_000;
/**
 * After someone lets go of a step, their last position for it stands until
 * this chart's own says the same (their save arrived) or this long. Longer
 * than GTM's 1.5 s: here a drop reaches the others only once it is saved
 * (0.9 s after the last change) and announced.
 */
export const DROP_GRACE_MS = 3_000;
/** How quickly a drawn drag catches up with the last position heard (ms to close ~63% of the gap). */
const DRAG_LAG_MS = 40;

/** The ink that reads on `color` (#rrggbb): near-black on a light colour, white on a dark one. */
export function inkOn(color: string): string {
  const value = Number.parseInt(color.slice(1), 16);
  if (color.length !== 7 || Number.isNaN(value)) return "#ffffff";
  const luma = 0.299 * ((value >> 16) & 255) + 0.587 * ((value >> 8) & 255) + 0.114 * (value & 255);
  return luma > 150 ? "#111114" : "#ffffff";
}

const same = (a: Point, b: Point) => Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;

export interface SeenCursor {
  readonly x: number;
  readonly y: number;
  /** When it last moved. */
  readonly at: number;
}

/** Each peer's pointer and when it last moved; one gone or off the chart (null) drops out. */
export function trackCursors(
  previous: ReadonlyMap<string, SeenCursor>,
  peers: ReadonlyArray<WorkflowPeer>,
  now: number,
): Map<string, SeenCursor> {
  const next = new Map<string, SeenCursor>();
  for (const peer of peers) {
    const cursor = peer.state.cursor;
    if (!cursor) continue;
    const seen = previous.get(peer.connection);
    next.set(
      peer.connection,
      seen && same(seen, cursor) ? seen : { x: cursor.x, y: cursor.y, at: now },
    );
  }
  return next;
}

/** The pointers to draw: present, and moved within CURSOR_IDLE_MS. */
export function liveCursors(
  seen: ReadonlyMap<string, SeenCursor>,
  peers: ReadonlyArray<WorkflowPeer>,
  now: number,
): Array<{ readonly peer: WorkflowPeer; readonly at: SeenCursor }> {
  return peers.flatMap((peer) => {
    const at = seen.get(peer.connection);
    return at && now - at.at < CURSOR_IDLE_MS ? [{ peer, at }] : [];
  });
}

/** A step someone is dragging (or just let go of): where they last had it, and where it is drawn now. */
export interface DragTrack {
  readonly peer: Pick<WorkflowPeer, "connection" | "name" | "color">;
  readonly target: Point;
  readonly shown: Point;
  /** When they let go; null while they still hold it. */
  readonly releasedAt: number | null;
}

/**
 * The steps others drag, from their presence: a new one starts where the
 * step stands, so it glides from there. One let go (no longer in anyone's
 * drag, or its person gone) keeps its last position until this chart's own
 * position for the step says the same or DROP_GRACE_MS passes, so it never
 * snaps back in between. A step no longer on the chart goes at once.
 */
export function trackDrags(
  previous: ReadonlyMap<string, DragTrack>,
  peers: ReadonlyArray<WorkflowPeer>,
  positions: ReadonlyMap<string, Point>,
  now: number,
): Map<string, DragTrack> {
  const next = new Map<string, DragTrack>();
  for (const peer of peers) {
    const drag = peer.state.drag;
    const at = drag ? positions.get(drag.id) : undefined;
    if (!drag || !at || next.has(drag.id)) continue;
    const before = previous.get(drag.id);
    next.set(drag.id, {
      peer: { connection: peer.connection, name: peer.name, color: peer.color },
      target: { x: drag.at.x, y: drag.at.y },
      shown: before?.shown ?? at,
      releasedAt: null,
    });
  }
  for (const [stepId, track] of previous) {
    if (next.has(stepId)) continue;
    const at = positions.get(stepId);
    if (!at || same(at, track.target)) continue;
    const releasedAt = track.releasedAt ?? now;
    if (now - releasedAt < DROP_GRACE_MS) next.set(stepId, { ...track, releasedAt });
  }
  return next;
}

/** One frame of the glide: each drawn drag closes on its target; null once every one stands on it. */
export function glide(
  tracks: ReadonlyMap<string, DragTrack>,
  elapsed: number,
): Map<string, DragTrack> | null {
  const share = 1 - Math.exp(-elapsed / DRAG_LAG_MS);
  let moving = false;
  const next = new Map<string, DragTrack>();
  for (const [stepId, track] of tracks) {
    if (track.shown === track.target) {
      next.set(stepId, track);
      continue;
    }
    moving = true;
    // Close enough: it lands on the target itself.
    if (same(track.shown, track.target)) {
      next.set(stepId, { ...track, shown: track.target });
      continue;
    }
    next.set(stepId, {
      ...track,
      shown: {
        x: track.shown.x + (track.target.x - track.shown.x) * share,
        y: track.shown.y + (track.target.y - track.shown.y) * share,
      },
    });
  }
  return moving ? next : null;
}

/** Whether someone else holds a step (they are dragging it): it is theirs until they let go. */
export const heldByOther = (peers: ReadonlyArray<WorkflowPeer>, stepId: string) =>
  peers.some((peer) => peer.state.drag?.id === stepId);

/** Everyone here once per person (a person's two tabs are one face), in the order they came. */
export function peopleOf(peers: ReadonlyArray<WorkflowPeer>): ReadonlyArray<WorkflowPeer> {
  const seen = new Set<string>();
  return peers.filter((peer) => {
    if (seen.has(peer.person)) return false;
    seen.add(peer.person);
    return true;
  });
}
